import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Icon, { iconPropTypes, ICON_OPTIONS } from '../../atoms/Icon';
import useTextInputLogic from './useTextInputLogic';
import { SIZES } from '../../../../../../src/theme';

const TEXT_INPUT_OPTIONS = {
  SIZES,
};

const StyledContainer = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  &:hover i {
    color: ${props => props.theme.palette.primary};
    transition: ${props =>
      props.theme.transition.create(['color', 'weight'], {
        duration: props.theme.transition.duration.shortest,
        timingFunction: props.theme.transition.timingFunction.sharp,
      })};
  }

  /** FULL WIDTH */
  ${props =>
    props.fullWidth &&
    css`
      width: 100%;
    `}

  /** PADDING */
  padding: 4px 12px; //TODO: adjust based on size prop
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  color: ${props => props.theme.text.normal};
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;

  /** SIZE */
  font-size: ${props => props.theme.fontSize[props.size]};

  /** WITH ICON */
  ${props =>
    props.hasIcon &&
    css`
      margin-left: ${props.theme.margin.normal};
    `}
`;

/**
 * NOTE: This component MUST be a child of `<FormProvider {...useForm()} />` from react-hook-form
 */
const TextInput = forwardRef(
  (
    {
      autoComplete,
      className,
      defaultValue,
      fullWidth,
      handleBlur,
      handleChange,
      handleFocus,
      icon,
      id,
      placeholder: defaultPlaceholder,
      size,
    },
    ref
  ) => {
    const {
      hasIcon,
      isFocused,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      register,
    } = useTextInputLogic({
      defaultPlaceholder,
      handleBlur,
      handleChange,
      handleFocus,
      icon,
      id,
    });

    const iconColor = isFocused
      ? ICON_OPTIONS.COLORS.PRIMARY
      : ICON_OPTIONS.COLORS.NORMAL;

    return (
      <StyledContainer className={className} fullWidth={fullWidth}>
        {hasIcon && <Icon color={iconColor} icon={icon} size={size} />}
        <StyledInput
          autoComplete={autoComplete}
          defaultValue={defaultValue}
          hasIcon={hasIcon}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeholder}
          ref={ref}
          size={size}
          {...register}
        />
      </StyledContainer>
    );
  }
);

const textInputPropTypes = {
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  fullWidth: PropTypes.bool,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  handleFocus: PropTypes.func,
  id: PropTypes.string.isRequired,
  icon: iconPropTypes.icon,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(Object.values(TEXT_INPUT_OPTIONS.SIZES)),
};
const textInputDefaultProps = {
  autoComplete: 'off',
  className: undefined,
  defaultValue: undefined,
  fullWidth: false,
  handleBlur: undefined,
  handleChange: undefined,
  handleFocus: undefined,
  icon: undefined,
  placeholder: undefined,
  size: TEXT_INPUT_OPTIONS.SIZES.NORMAL,
};

TextInput.propTypes = textInputPropTypes;
TextInput.defaultProps = textInputDefaultProps;
export default TextInput;
export { TEXT_INPUT_OPTIONS, textInputPropTypes, textInputDefaultProps };
